import {
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { useUserDevices, useUserInbox } from '@/hooks/useUsers';
import { formatOrderDate } from '@liminal/utils/date';
import { StatusPill, type StatusPillTone } from '@liminal/web-ui/primitives/StatusPill';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import type { Device, InboxItem } from '@/api/types';

function platformLabel(platform?: string): string {
  if (!platform) return 'Unknown';
  return platform.replace('PLATFORM_', '');
}

function platformVariant(platform?: string): StatusPillTone {
  if (!platform) return 'neutral';
  if (platform.includes('IOS')) return 'info';
  if (platform.includes('ANDROID')) return 'success';
  if (platform.includes('WEB')) return 'warning';
  return 'neutral';
}

function channelLabel(channel?: string): string {
  if (!channel) return 'Unknown';
  return channel.replace('CHANNEL_', '').replace(/_/g, ' ');
}

/** Both responses carry the user's whole set, so these are true sorts. */
const DEVICE_SORT_ACCESSORS = {
  platform: (device: Device) => platformLabel(device.platform),
  appVersion: (device: Device) => device.appVersion,
  osVersion: (device: Device) => device.osVersion,
  channel: (device: Device) => channelLabel(device.channel),
  isActive: (device: Device) => device.isActive ?? false,
  provider: (device: Device) => device.provider,
  lastSeenAt: (device: Device) => device.lastSeenAt,
};

const INBOX_SORT_ACCESSORS = {
  type: (item: InboxItem) => item.type,
  title: (item: InboxItem) => item.title,
  body: (item: InboxItem) => item.body,
  isRead: (item: InboxItem) => item.isRead ?? false,
  createdAt: (item: InboxItem) => item.createdAt,
};

// One array per table drives its header cells and the DataTableBody column count.
const DEVICE_COLUMNS = [
  { column: 'platform', label: 'Platform' },
  { column: 'appVersion', label: 'App Version' },
  { column: 'osVersion', label: 'OS Version' },
  { column: 'channel', label: 'Channel' },
  { column: 'isActive', label: 'Active' },
  { column: 'provider', label: 'Provider' },
  { column: 'lastSeenAt', label: 'Last Seen' },
] as const;

const INBOX_COLUMNS = [
  { column: 'type', label: 'Type' },
  { column: 'title', label: 'Title' },
  { column: 'body', label: 'Body' },
  { column: 'isRead', label: 'Read' },
  { column: 'createdAt', label: 'Created' },
] as const;

export function NotificationsSection({ userId }: { userId: string }) {
  const {
    data: devicesData,
    isLoading: devicesLoading,
    isError: devicesError,
  } = useUserDevices(userId);
  const { data: inboxData, isLoading: inboxLoading, isError: inboxError } = useUserInbox(userId);

  const { rows: devices, sort: deviceSort } = useTableSort(
    devicesData?.devices ?? [],
    DEVICE_SORT_ACCESSORS,
    'lastSeenAt',
    'desc',
  );
  const { rows: items, sort: inboxSort } = useTableSort(
    inboxData?.items ?? [],
    INBOX_SORT_ACCESSORS,
    'createdAt',
    'desc',
  );

  // The two tables load and fail independently, and a failed request says so
  // instead of reading as "No devices registered".
  return (
    <Stack spacing={3}>
      {/* Devices */}
      <Card variant="elevated" padding="none">
          <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
            Registered Devices
            {devicesData && (
              <Typography component="span" variant="body2" color="text.disabled" sx={{ ml: 1 }}>
                ({devices.length})
              </Typography>
            )}
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                {DEVICE_COLUMNS.map(({ column, label }) => (
                  <SortableTableCell key={column} column={column} sort={deviceSort}>
                    {label}
                  </SortableTableCell>
                ))}
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={DEVICE_COLUMNS.length}
              isLoading={devicesLoading}
              isError={devicesError}
              isEmpty={devices.length === 0}
              emptyMessage="No devices registered."
              errorMessage="Could not load this user's devices."
            >
              {devices.map((d, i) => (
                <TableRow key={d.id ?? i}>
                  <TableCell>
                    <StatusPill
                      label={platformLabel(d.platform)}
                      tone={platformVariant(d.platform)}
                      size="sm"
                    />
                  </TableCell>
                  <TableCell>{d.appVersion || '—'}</TableCell>
                  <TableCell>{d.osVersion || '—'}</TableCell>
                  <TableCell>{channelLabel(d.channel)}</TableCell>
                  <TableCell>
                    <StatusPill
                      label={d.isActive ? 'ACTIVE' : 'INACTIVE'}
                      tone={d.isActive ? 'success' : 'error'}
                      size="sm"
                    />
                  </TableCell>
                  <TableCell>{d.provider || '—'}</TableCell>
                  <TableCell>{d.lastSeenAt ? formatOrderDate(d.lastSeenAt) : '—'}</TableCell>
                </TableRow>
              ))}
            </DataTableBody>
          </Table>
      </Card>

      {/* Inbox */}
      <Card variant="elevated" padding="none">
          <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
            Inbox
            {inboxData && (
              <Typography component="span" variant="body2" color="text.disabled" sx={{ ml: 1 }}>
                ({inboxData.total ?? 0} total, {inboxData.unreadCount ?? 0} unread)
              </Typography>
            )}
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                {INBOX_COLUMNS.map(({ column, label }) => (
                  <SortableTableCell key={column} column={column} sort={inboxSort}>
                    {label}
                  </SortableTableCell>
                ))}
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={INBOX_COLUMNS.length}
              isLoading={inboxLoading}
              isError={inboxError}
              isEmpty={items.length === 0}
              emptyMessage="No inbox items."
              errorMessage="Could not load this user's inbox."
            >
              {items.map((item, i) => (
                <TableRow key={item.id ?? i}>
                  <TableCell>
                    <StatusPill label={item.type || 'unknown'} tone="neutral" size="sm" />
                  </TableCell>
                  <TableCell>{item.title || '—'}</TableCell>
                  <TableCell
                    sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {item.body || '—'}
                  </TableCell>
                  <TableCell>
                    <StatusPill
                      label={item.isRead ? 'READ' : 'UNREAD'}
                      tone={item.isRead ? 'neutral' : 'warning'}
                      size="sm"
                    />
                  </TableCell>
                  <TableCell>{item.createdAt ? formatOrderDate(item.createdAt) : '—'}</TableCell>
                </TableRow>
              ))}
            </DataTableBody>
          </Table>
      </Card>
    </Stack>
  );
}
